import { useState, useEffect } from "react";
import { Check, Tv, Search, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { fetchProviders } from "@/lib/tmdb";
import type { TMDBProvider } from "@/lib/tmdb";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ProviderPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  region: string;
  selectedIds: number[];
  onSave: (ids: number[]) => void;
}

const ProviderPicker = ({ open, onOpenChange, region, selectedIds, onSave }: ProviderPickerProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [providers, setProviders] = useState<TMDBProvider[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<number[]>(selectedIds);

  useEffect(() => {
    if (open) {
      setSelected(selectedIds);
      setLoading(true);
      fetchProviders(region)
        .then(setProviders)
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [open, region, selectedIds]);

  const toggle = (id: number) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]);
  };

  const filtered = providers.filter((p) =>
    p.provider_name.toLowerCase().includes(search.toLowerCase())
  );

  const handleSave = () => {
    onSave(selected);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm mx-auto rounded-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tv className="w-5 h-5 text-primary" />
            {t("providerPicker.title")}
          </DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder={t("providerPicker.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-secondary border border-border text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>

        <div className="flex items-center justify-between">
          {selected.length > 0 ? (
            <p className="text-xs text-primary font-medium">{selected.length} {t("providerPicker.selected")}</p>
          ) : <span />}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSelected(filtered.map((p) => p.provider_id))}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {t("providerPicker.selectAll", "Select all")}
            </button>
            <span className="text-xs text-border">|</span>
            <button
              type="button"
              onClick={() => setSelected([])}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {t("providerPicker.deselectAll", "Deselect all")}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2 flex-1 overflow-y-auto max-h-[45vh]">
            {filtered.map((provider) => {
              const active = selected.includes(provider.provider_id);
              return (
                <button
                  key={provider.provider_id}
                  onClick={() => toggle(provider.provider_id)}
                  className={`flex items-center gap-2.5 p-2.5 rounded-xl transition-all active:scale-95 ${
                    active
                      ? "bg-primary/15 border-2 border-primary"
                      : "bg-secondary/50 border-2 border-transparent hover:border-border"
                  }`}
                >
                  {provider.logo_path ? (
                    <img src={provider.logo_path} alt={provider.provider_name} className="w-9 h-9 rounded-lg object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                      <Tv className="w-4 h-4 text-muted-foreground" />
                    </div>
                  )}
                  <span className="text-xs font-medium text-foreground truncate">{provider.provider_name}</span>
                </button>
              );
            })}
          </div>
        )}

        <button
          onClick={handleSave}
          disabled={selected.length === 0}
          className="w-full py-3 rounded-full bg-gradient-iris text-primary-foreground font-semibold text-sm shadow-iris active:scale-95 transition-transform disabled:opacity-40"
        >
          {t("common.save")} ({selected.length})
        </button>
      </DialogContent>
    </Dialog>
  );
};

export default ProviderPicker;
